import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Users, Wrench, Star, ChevronRight, Play, ExternalLink, KeyRound, MapPin, Route } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API, formatPrice } from '@/lib/utils';
import usePageMeta from '@/lib/usePageMeta';
import NewsletterSignup from '@/components/NewsletterSignup';
import { track } from '@/lib/track';

const Home = () => {
  usePageMeta({
    title: 'Factory-Direct Custom Luxury Van Conversions',
    description: 'Factory-direct luxury van conversions with no dealership hassle. Legend Conversion Corp builds custom luxury vans at our Alvarado, Texas factory — executive limos, family travel vans, and commercial shuttles at factory-direct pricing.',
    keywords: 'factory direct van conversion, custom van, luxury van conversion, custom van builder alvarado, mercedes van conversion, executive sprinter limo, custom van texas, buy van direct from factory',
  });
  const [models, setModels] = useState([]);
  const storyVideoRef = useRef(null);

  const handleWatchStory = () => {
    const target = document.getElementById('brand-story');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      if (storyVideoRef.current) {
        storyVideoRef.current.muted = false;
        storyVideoRef.current.currentTime = 0;
        storyVideoRef.current.play().catch(() => {});
      }
    }, 900);
  };

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API}/models`);
        setModels(response.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };
    fetchModels();
  }, []);

  const features = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Expert Customization",
      description: "Over 31 years of experience in RV and conversion industries, delivering unmatched craftsmanship."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Unmatched Quality",
      description: "Premium materials and meticulous attention to detail in every build we create."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Family Owned",
      description: "A family-owned company in Alvarado, TX dedicated to building relationships."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "3-Year Warranty",
      description: "Comprehensive 3-Year, 36,000-Mile Warranty on all conversion work."
    }
  ];

  const testimonials = [
    {
      name: "Legend Conversion Client",
      role: "Custom Van Owner",
      text: "My family and I had the absolute best experience working with Kent Ellington at the factory. He made the entire process so enjoyable, and we were able to design our van exactly the way I envisioned\u2014it was truly such a fun and memorable experience!",
      rating: 5
    },
    {
      name: "Legend Conversion Client",
      role: "Custom Van Owner",
      text: "From start to finish, the team at Legend was incredible. You can really tell how much pride they take in their work\u2014the craftsmanship and attention to detail are outstanding. The entire staff made us feel welcomed and taken care of every step of the way.",
      rating: 5
    },
    {
      name: "Legend Conversion Client",
      role: "Custom Van Owner",
      text: "One of the biggest surprises was the pricing\u2014it was actually better than what we found at dealerships, which made the experience even more rewarding. Getting a fully customized van, built exactly how we wanted, at a better value was something we didn't expect but truly appreciated.",
      rating: 5
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Video (muted autoplay loop) with image poster fallback */}
        <div className="absolute inset-0">
          <video
            data-testid="hero-bg-video"
            ref={(el) => {
              if (el && el.paused) el.play().catch(() => {});
            }}
            src="/videos/legend-conversion-2026.mp4"
            poster="https://images.pexels.com/photos/19871521/pexels-photo-19871521.jpeg?auto=compress&cs=tinysrgb&w=1280"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center text-white">
          <p className="text-gold font-accent tracking-[0.3em] text-sm md:text-base mb-6 animate-fade-in">
            CUSTOM LUXURY VAN CONVERSIONS &bull; BUILT IN TEXAS
          </p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
            Factory-Direct Luxury.<br />
            <span className="text-gold">No Dealership Hassle.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 animate-fade-in-up stagger-2">
            Skip the middleman and the markup. Every Legend van is designed, built, and sold direct from our Texas factory &mdash; first class on four wheels, at a price no dealership can touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-3">
            <Link to="/build">
              <Button 
                data-testid="hero-cta-build"
                className="btn-gold text-lg px-10 py-6"
              >
                Build Your Legend
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/models">
              <Button 
                data-testid="hero-cta-explore"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-navy text-lg px-10 py-6"
              >
                Explore Models
              </Button>
            </Link>
            <Button
              data-testid="hero-cta-watch-story"
              variant="ghost"
              onClick={handleWatchStory}
              className="text-white hover:bg-white/10 text-lg px-6 py-6 gap-2 group"
            >
              <span className="relative w-9 h-9 rounded-full border border-white/40 flex items-center justify-center group-hover:border-gold transition-colors">
                <Play className="w-3.5 h-3.5 text-white group-hover:text-gold transition-colors fill-current" />
              </span>
              Watch the Full Story
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full" />
          </div>
        </div>
      </section>

      {/* Factory-Direct Advantage Section */}
      <section data-testid="factory-direct-section" className="relative bg-navy py-24 overflow-hidden noise-overlay">
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">THE FACTORY-DIRECT ADVANTAGE</p>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-white mb-6">
                Why Pay a Dealership Markup?
              </h2>
              <p className="text-white/70 text-lg mb-4">
                At a dealership, you pay more for a van built for anyone. At Legend, you buy straight from the factory floor &mdash; working directly with the craftsmen who build your van, with every dollar going into the vehicle instead of a middleman.
              </p>
              <p className="text-white/70 text-lg mb-8">
                Tour our showroom, walk the production line, and drive away in a van built around you.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Link to="/build">
                  <Button className="btn-gold" data-testid="factory-direct-build-cta">
                    Start Your Build
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/models">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-navy" data-testid="factory-direct-models-cta">
                    Browse Models
                  </Button>
                </Link>
              </div>
            </div>
            <div className="space-y-6">
              {[
                {
                  num: '01',
                  title: 'No Dealer Markup',
                  text: 'Buy direct from the builder and skip the dealership fees, add-ons, and negotiation games.'
                },
                {
                  num: '02',
                  title: 'Talk to the Actual Builders',
                  text: 'Work one-on-one with the team crafting your van — not a salesperson reading a spec sheet.'
                },
                {
                  num: '03',
                  title: 'Built Around You',
                  text: 'Choose your colors, seating, entertainment, and finishes. Your van, your way, from day one.'
                },
                {
                  num: '04',
                  title: 'Factory-Backed Warranty',
                  text: 'Every conversion is covered by our 3-Year, 36,000-Mile Warranty — direct from the people who built it.'
                }
              ].map((item) => (
                <div
                  key={item.num}
                  data-testid={`factory-direct-benefit-${item.num}`}
                  className="flex gap-6 items-start bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-sm hover:border-gold/40 transition-colors duration-300"
                >
                  <span className="font-display text-gold text-2xl font-bold leading-none pt-1">{item.num}</span>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="section-padding bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">WHY CHOOSE LEGEND</p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-navy mb-6">
              The Legend Difference
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              We&apos;re not just building vans; we&apos;re building relationships, providing the highest quality craftsmanship and a customer experience that makes you part of our family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={feature.title || index}
                data-testid={`feature-card-${index}`}
                className="card-hover border-0 shadow-sm bg-surface group"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-navy/5 rounded-full flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-navy mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Models Preview Section */}
      <section data-testid="models-preview-section" className="section-padding bg-surface">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-12 gap-6">
            <div>
              <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">OUR MODELS</p>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-navy">
                Featured Conversions
              </h2>
            </div>
            <Link to="/models">
              <Button variant="ghost" className="text-navy hover:text-gold group">
                View All Models
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {models.map((model, index) => (
              <Link 
                key={model.id}
                to={`/models/${model.id}`}
                data-testid={`model-card-${model.id}`}
                className="group"
              >
                <Card className="overflow-hidden card-hover border-0 shadow-md h-full">
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img
                      src={model.image}
                      alt={model.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gold text-xs font-bold tracking-wider mb-2">
                      {model.passengers} PASSENGERS
                    </p>
                    <h3 className="font-display text-xl font-semibold text-navy mb-2 group-hover:text-gold transition-colors">
                      {model.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {model.tagline}
                    </p>
                    <p className="text-navy font-bold">
                      Call for Pricing
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section id="brand-story" data-testid="video-section" className="relative py-32 bg-navy overflow-hidden noise-overlay scroll-mt-24">
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">THE BRAND STORY</p>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-white mb-6">
                Inside a Legend Build
              </h2>
              <p className="text-white/70 text-lg mb-8">
                Every Legend conversion starts with a premium chassis and ends with an extraordinary vehicle. Watch our full brand film to see the craftsmanship, the team, and the details that go into each one.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Link to="/gallery">
                  <Button className="btn-gold" data-testid="video-section-gallery-cta">
                    View Gallery
                  </Button>
                </Link>
                <Link to="/models">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-navy" data-testid="video-section-models-cta">
                    Browse Models
                  </Button>
                </Link>
              </div>
              <NewsletterSignup variant="brand-story" source="brand_story_video" />
            </div>
            <div className="relative aspect-video rounded-lg overflow-hidden shadow-2xl bg-black">
              <video
                ref={storyVideoRef}
                data-testid="brand-story-video"
                src="/videos/legend-conversion-2026.mp4"
                poster="/gallery/traveler-luxe/traveler-luxe-02.webp"
                controls
                preload="metadata"
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Van Rentals Section */}
      <section id="van-rentals" data-testid="van-rentals-section" className="section-padding bg-surface scroll-mt-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">VAN RENTALS</p>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-navy mb-6">
                Your Next Trip Starts Here.
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Looking to rent a van? Explore rental options with Regency Rental.
              </p>
              <a
                href="https://regencyrental.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="van-rentals-cta"
                aria-label="Explore Regency Rental (opens in a new tab)"
                onClick={() => track('van_rental_click', { placement: 'home_van_rentals_section', destination: 'regencyrental.com' })}
                className="inline-block"
              >
                <Button className="btn-gold gap-2">
                  Explore Regency Rental
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </Button>
              </a>
              <p className="text-sm text-muted-foreground mt-4 mb-6">
                You&rsquo;ll continue to Regency Rental for rental details and inquiries.
              </p>
              <Link
                to="/models"
                data-testid="van-rentals-models-link"
                className="inline-flex items-center gap-1.5 text-navy font-semibold hover:text-gold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold rounded-sm"
              >
                Prefer a custom build? Explore our models
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="relative rounded-lg overflow-hidden bg-navy noise-overlay shadow-2xl min-h-[280px] lg:min-h-[340px] flex items-center justify-center p-10">
              <div className="relative z-10 text-center">
                <div className="mx-auto w-20 h-20 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-6">
                  <KeyRound className="w-9 h-9 text-gold" aria-hidden="true" />
                </div>
                <p className="text-gold font-accent tracking-[0.25em] text-xs mb-3">REGENCY RENTAL</p>
                <p className="text-white/80 text-sm max-w-xs mx-auto">
                  Rentals are provided by Regency Rental — a separate van rental company.
                </p>
                <div className="flex items-center justify-center gap-6 mt-6 text-white/40">
                  <MapPin className="w-5 h-5" aria-hidden="true" />
                  <Route className="w-5 h-5" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="section-padding bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-gold font-accent tracking-[0.2em] text-sm mb-4">TESTIMONIALS</p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-navy mb-6">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={`${testimonial.name || 'testimonial'}-${index}`}
                data-testid={`testimonial-${index}`}
                className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-gold fill-gold" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">
                    &ldquo;{testimonial.text}&rdquo;
                  </p>
                  <div>
                    <p className="font-semibold text-navy">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/testimonials">
              <Button variant="outline" className="border-gold/40 text-navy hover:bg-gold/5 gap-2 font-semibold">
                Read All Reviews
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA handled by Footer — no duplicate needed here */}
    </div>
  );
};

export default Home;
